import React, { FC, useEffect, useState } from "react";
import {
  Container,
  NavWrapper
} from './styles'
import { Button, Typography, TopProgress, Tabs } from "../../components";
import { useTranslation } from "react-i18next";
import { affiliateService } from "../../api";
import { useUi } from "../../context/ui.context";
import {
  calculateScales,
  createPercentageArrays,
} from './metricsFuncs'
import { formatCurrency } from "../../utils/display-price";
import { useAuth } from "../../context/user.context";
import { filterByToday, timeConverterMMDDYYHHMM } from "../../utils/dates";
import { Route, Navigate, Routes, useLocation } from "react-router-dom";
import {Overview, Conversions, Customers, NoAffiliate, InfoAffiliate, CustomerPage, ConversionPage} from './components'
import {
  StateWrapper,
  GreenDot,
  Greentext
} from "../Instances/styles";
import { handleStatusName } from "../../utils/handle-status-name";


const Affiliate:FC<any> = () => {
  const [t] = useTranslation()
  const {user} = useAuth()
  const [programData, setProgramData] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [defaultConfig, setDefaultConfig] = useState<any>()
  const [networkMetrics, setNetworkMetrics] = useState<any[]>([])
  const [networkMetricsOut, setNetworkMetricsOut] = useState<any[]>([])
  const [loadNetwork, setLoadNetwork] = useState<boolean>(false)
  const [scales, setScales] = useState<any[]>([])
  const [countries, setCountries] = useState<any[]>([])
  const {viewAlert} = useUi()
  const location = useLocation()
  
  
  const getCommissionsData = async () => {
    try {
      const commissionsRes = await affiliateService.getComissions()
      const data = commissionsRes?.data
      return { results: data?.results || [], count: data?.count || 0 }
    } catch (e: any) {
      console.error('Error fetching commissions:', e)
      return { results: [], count: 0 }
    }
  }

  const getCustomersData = async () => {
    try {
      const customersRes = await affiliateService.getCustomers()
      const data = customersRes?.data
      return data?.results || []
    } catch (e: any) {
      console.error('Error fetching customers:', e)
      return []
    }
  }

  const getRefData = async () => {
    try{
      const resDefault = await affiliateService.getDefaultConfig()
      const defaultData = resDefault?.data
      setDefaultConfig(defaultData)
      const refRes = await affiliateService.getRef()
      const data = refRes?.data
      
      const [commissionsData, referrals] = await Promise.all([
        getCommissionsData(),
        getCustomersData()
      ])
      const transactions = commissionsData.results
      const transactionsCount = commissionsData.count
      
      const balances = data?.balances?.map((balance: any) => ({
        ...balance,
        total_balance: balance.total_balance,
        available_balance: balance.available_balance,
        pending_balance: balance.pending_balance,
        formatted_total: formatCurrency(balance.total_balance, balance.currency),
        formatted_available: formatCurrency(balance.available_balance, balance.currency),
        formatted_pending: formatCurrency(balance.pending_balance, balance.currency)
      })) || []
      
      const customers = referrals?.map((referral: any) => {
        return {
          ...referral,
          customer_id: referral.user?.id,
          created: timeConverterMMDDYYHHMM(referral.created_at),
          country: referral.client?.country || referral.region || 'unknown',
          status: (<StateWrapper>
            <GreenDot active={referral.status === 'approved' ? 'true' : 'false'}/>
            <Greentext active={referral.status === 'approved' ? 'true' : 'false'}>
              {handleStatusName(referral.status, t)}
            </Greentext>
          </StateWrapper>)
        }
      }) || []
      
      const referralsMap = new Map()
      referrals.forEach((referral: any) => {
        referralsMap.set(referral.user?.id, referral)
      })

      const conversions = transactions.map((transaction: any) => {
        const relatedReferral = referralsMap.get(transaction.user_id) || null
        
        return {
          ...transaction,
          amount: `${parseFloat(transaction.amount).toFixed(2)} ${transaction.currency}`,
          customer_id: relatedReferral?.user?.id || 'N/A',
          external_id: transaction.id,
          country: relatedReferral?.client?.country || relatedReferral?.region || 'unknown',
          created: timeConverterMMDDYYHHMM(transaction.created_at)
        }
      })
      
      const commissionTransactions = transactions.filter((t: any) => {
        return t.status === 'approved';
      });
      
      const commissionAmountDaily = filterByToday(commissionTransactions, 'created_at')
        .reduce((total: any, item: any) => total + parseFloat(item.amount), 0)
      const commissionAmount = commissionTransactions
        .reduce((total: any, item: any) => total + parseFloat(item.amount), 0)
      
      const commissionByCurrency = commissionTransactions.reduce((acc: any, transaction: any) => {
        const currency = transaction.currency || 'USD'
        if (!acc[currency]) {
          acc[currency] = 0
        }
        acc[currency] += parseFloat(transaction.amount)
        return acc
      }, {})

      const commissionAmountDailyByCurrency = filterByToday(commissionTransactions, 'created_at')
        .reduce((acc: any, transaction: any) => {
          const currency = transaction.currency || 'USD'
          if (!acc[currency]) {
            acc[currency] = 0
          }
          acc[currency] += parseFloat(transaction.amount)
          return acc
        }, {})

      const comissionAmountFromBalances = balances
        .map((b: any) => formatCurrency(b.available_balance, b.currency))
        .join(' + ')

      setProgramData({
        info: data,
        link:`https://onecloudplanet.com?ref=${data.code}`,
        balances: balances,
        comission: commissionTransactions,
        customers: customers,
        conversion: conversions,
        conversionCount: transactionsCount,
        comissionAmountDaily: Object.entries(commissionAmountDailyByCurrency)
          .map(([currency, amount]: [string, any]) =>
            formatCurrency(amount.toFixed(2), currency as 'EUR' | 'USD' | 'PLN' | 'UAH')
          ).join(' + '),
        comissionAmount: comissionAmountFromBalances
      })
      
      setScales(calculateScales(commissionTransactions))
      
      const countryData = referrals.reduce((acc: any, referral: any) => {
        const country = referral.client?.country || referral.region || 'unknown'
        if (!acc[country]) {
          acc[country] = 0
        }
        acc[country] += 1
        return acc
      }, {})

      const countriesData = Object.entries(countryData).map(([country, value]) => ({
        country: country.toLowerCase(),
        value: value
      }))

      setCountries(countriesData.length > 0 ? countriesData : [{
        country: 'unknown',
        value: 1
      }])
      
      const createAffiliateMetrics = (transactions: any[]) => {
        const msInDay = 24 * 60 * 60 * 1000;
        const now = new Date();
        const last28Days = [...Array(28)].map((_, i) => new Date(now.getTime() - msInDay * i).toISOString());

        if (transactions.length === 0) {
          return last28Days.map((date, index) => {
            const percentValue = (index + 1) * (100 / 27);
            return [percentValue, 0, date, '0.00'];
          });
        }

        const dailyTotals = last28Days.map((date) => {
          const dailyData = transactions.filter((item: any) => {
            const itemDate = new Date(item.created_at);
            const targetDate = new Date(date);
            return itemDate.getDate() === targetDate.getDate() &&
                   itemDate.getMonth() === targetDate.getMonth() &&
                   itemDate.getFullYear() === targetDate.getFullYear();
          });
          return dailyData.reduce((acc: any, curr: any) => acc + parseFloat(curr.amount || 0), 0);
        });

        const maxTotal = Math.max(...dailyTotals, 70);

        const result = last28Days.map((date, index) => {
          const total = dailyTotals[index];
          const xPercent = (index + 1) * (100 / 27);
          const yPercent = (total / maxTotal) * 100;

          return [xPercent, yPercent, date, Number(total).toFixed(2)];
        });

        return result;
      }
      
      const affiliateMetrics = createAffiliateMetrics(commissionTransactions)
      setNetworkMetrics(affiliateMetrics)
      setNetworkMetricsOut(affiliateMetrics)
      
    } catch (e:any) {
      // viewAlert({
      //   severity: 'error',
      //   message: e?.response?.data?.detail || t("ERROR_OCCURRED")
      // })
    } finally {
      setPageLoading(false)
    }

  }

  useEffect(() => {
    getRefData()
  }, [])

  const tabs = [
    {
      title:t("OVERVIEW"),
      slug:'overview',
      id:'1'
    },
    {
      title:t("CONVERSIONS"),
      slug:'conversions',
      id:'2'
    },
    {
      title:t("CUSTOMERS"),
      slug:'customers',
      id:'3'
    },
    {
      title:t("INFO"),
      slug:'info',
      id:"4"
    }
  ]
  const activeTab = tabs.find((item:any) => location.pathname.includes(item.slug))?.slug || 'overview'
  if(pageLoading) return <TopProgress loading={pageLoading}/>
  if(!programData) return (<NoAffiliate defaultConfig={defaultConfig} setPageLoading={setPageLoading} getRefData={getRefData}/>)
  return(
    <Container>
      <Typography variant="h1">
        {t("AFFILIATE")}
      </Typography>
      <NavWrapper>
        <div>
          <Tabs
            tabs={tabs}
            size='display'
            location={activeTab}
            onChange={() => {}}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to="overview" />} /> 
        <Route path='/overview' element={<Overview 
          programData={programData}
          scales={scales}
          networkMetricsOut={networkMetricsOut}
          networkMetrics={networkMetrics}
          loadNetwork={loadNetwork}
          countries={countries}
          defaultConfig={defaultConfig}
          />}/>
        <Route path='/conversions' element={<Conversions />}/>
        <Route path='/conversions/:conversionId' element={<ConversionPage />}/>
        <Route path='/customers' element={<Customers />}/>
        <Route path='/customers/:customerId' element={<CustomerPage />}/>
        <Route path='/info' element={<InfoAffiliate defaultConfig={defaultConfig}/>}/>
      </Routes>
    </Container>
  )
}

export default Affiliate
